import { queryKeys } from '@/lib/query-keys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EntityNote, FolderNote, FolderWithChildren, Note, NoteFolder } from '../supabase/notes';
import { supabase } from '../supabase/supabase';

// Define cache times - using the same aggressive caching as other queries
const HOUR_IN_MS = 1000 * 60 * 60;
const DAY_IN_MS = HOUR_IN_MS * 24;
const STALE_TIME = DAY_IN_MS * 30; // 30 days stale time
const GC_TIME = DAY_IN_MS * 60; // 60 days garbage collection

/**
 * Hook to fetch all notes
 */
export function useAllNotes() {
  return useQuery({
    queryKey: queryKeys.notes.list(),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('notes')
        .select('*')
        .order('updated_at', { ascending: false });

      if (error) throw error;
      return data as Note[];
    },
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

/**
 * Hook to fetch a note by ID
 */
export function useNote(noteId: number | string | null | undefined) {
  // Convert noteId to a number if it's a string
  const numericNoteId = noteId ? Number(noteId) : undefined;

  return useQuery({
    queryKey: queryKeys.notes.detail(numericNoteId || 0),
    queryFn: async () => {
      if (!numericNoteId) throw new Error('Note ID is required');

      console.log('Fetching note with ID:', numericNoteId);

      // Direct Supabase call to ensure it's working
      const { data, error } = await supabase
        .from('notes')
        .select('*')
        .eq('id', numericNoteId)
        .single();

      if (error) {
        console.error('Error fetching note:', error);
        throw error;
      }

      console.log('Note data fetched successfully:', data);
      return data as Note;
    },
    staleTime: 0, // Don't cache for now to ensure fresh data
    gcTime: GC_TIME,
    enabled: !!numericNoteId,
    retry: 3, // Retry failed requests up to 3 times
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
  });
}

/**
 * Hook to create a note
 */
export function useCreateNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      title = null,
      content = [],
      isPinned = false,
      projectId
    }: {
      title?: string | null;
      content?: any[];
      isPinned?: boolean;
      projectId: string | number;
    }) => {
      const { data, error } = await supabase
        .from('notes')
        .insert([
          {
            title,
            content,
            is_pinned: isPinned,
            project_id: projectId
          }
        ])
        .select()
        .single();

      if (error) throw error;
      return data as Note;
    },
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: queryKeys.notes.lists() });
    },
  });
}

/**
 * Hook to update a note
 */
export function useUpdateNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      noteId,
      entityId: _entityId,
      data
    }: {
      noteId: number;
      entityId?: string | number;
      data: Partial<Note>;
    }) => {
      const { data: updatedNote, error } = await supabase
        .from('notes')
        .update(data)
        .eq('id', noteId)
        .select()
        .single();

      if (error) throw error;
      return updatedNote as Note;
    },
    onSuccess: (data, variables) => {
      // Update the cache for this specific note
      queryClient.setQueryData(
        queryKeys.notes.detail(data.id),
        data
      );

      // Invalidate lists that might include this note
      queryClient.invalidateQueries({ queryKey: queryKeys.notes.lists() });

      // If we have an entityId, invalidate its specific notes list
      if (variables.entityId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.notes.byEntity(variables.entityId)
        });
      }
    },
  });
}

/**
 * Hook to delete a note
 */
export function useDeleteNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (noteId: number) => {
      const { error } = await supabase
        .from('notes')
        .delete()
        .eq('id', noteId);

      if (error) throw error;
      return noteId;
    },
    onSuccess: (noteId) => {
      // Remove the note from the cache
      queryClient.removeQueries({ queryKey: queryKeys.notes.detail(noteId) });

      // Invalidate lists that might include this note
      queryClient.invalidateQueries({ queryKey: queryKeys.notes.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.notes.all });
    },
  });
}

/**
 * Hook to fetch all notes for an entity
 */
export function useEntityNotes(entityId: string | number | null | undefined) {
  return useQuery({
    queryKey: queryKeys.notes.byEntity(entityId || ''),
    queryFn: async () => {
      if (!entityId) throw new Error('Entity ID is required');

      const { data, error } = await supabase
        .from('entity_notes')
        .select(`
          id,
          note_id,
          entity_id,
          created_at,
          note:note_id (*)
        `)
        .eq('entity_id', entityId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Extract the notes from the relationships
      const notes: Note[] = [];

      for (const item of data) {
        if (item.note && typeof item.note === 'object') {
          notes.push(item.note as unknown as Note);
        }
      }

      return notes;
    },
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    enabled: !!entityId,
  });
}

/**
 * Hook to get or create a note for an entity
 */
export function useGetOrCreateEntityNote(entityId: string | number | null | undefined) {
  const queryClient = useQueryClient();
  const entityNotesQuery = useEntityNotes(entityId);

  const createNoteMutation = useMutation({
    mutationFn: async (entityId: string | number) => {
      // Create a new note
      const { data: noteData, error: noteError } = await supabase
        .from('notes')
        .insert([
          {
            title: `Entity ${entityId} Notes`,
            content: [],
          }
        ])
        .select()
        .single();

      if (noteError) throw noteError;

      // Attach the note to the entity
      const { error: relationError } = await supabase
        .from('entity_notes')
        .insert([
          {
            note_id: noteData.id,
            entity_id: Number(entityId),
          }
        ])
        .select()
        .single();

      if (relationError) throw relationError;

      return noteData as Note;
    },
    onSuccess: (data, variables) => {
      // Update the entity notes cache
      queryClient.setQueryData(
        queryKeys.notes.byEntity(variables),
        (oldData: Note[] | undefined) => {
          return oldData ? [data, ...oldData] : [data];
        }
      );

      // Also update the note detail cache
      queryClient.setQueryData(
        queryKeys.notes.detail(data.id),
        data
      );
    },
  });

  return useQuery({
    queryKey: [...queryKeys.notes.byEntity(entityId || ''), 'getOrCreate'],
    queryFn: async () => {
      if (!entityId) throw new Error('Entity ID is required');

      // First try to get existing notes
      const notes = await entityNotesQuery.refetch();

      if (notes.data && notes.data.length > 0) {
        return notes.data[0];
      }

      // If no notes exist, create a new one
      return await createNoteMutation.mutateAsync(entityId);
    },
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    enabled: !!entityId && entityNotesQuery.isSuccess,
  });
}

/**
 * Hook to attach a note to an entity
 */
export function useAttachNoteToEntity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      noteId,
      entityId
    }: {
      noteId: number;
      entityId: number;
    }) => {
      const { data, error } = await supabase
        .from('entity_notes')
        .insert([
          {
            note_id: noteId,
            entity_id: entityId,
          }
        ])
        .select()
        .single();

      if (error) throw error;
      return data as EntityNote;
    },
    onSuccess: (_, variables) => {
      // Invalidate the entity notes query
      queryClient.invalidateQueries({
        queryKey: queryKeys.notes.byEntity(variables.entityId),
      });
    },
  });
}

/**
 * Hook to detach a note from an entity
 */
export function useDetachNoteFromEntity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      noteId,
      entityId
    }: {
      noteId: number;
      entityId: number;
    }) => {
      const { error } = await supabase
        .from('entity_notes')
        .delete()
        .eq('note_id', noteId)
        .eq('entity_id', entityId);

      if (error) throw error;
      return { noteId, entityId };
    },
    onSuccess: (_, variables) => {
      // Invalidate the entity notes query
      queryClient.invalidateQueries({
        queryKey: queryKeys.notes.byEntity(variables.entityId),
      });
    },
  });
}

/**
 * Hook to fetch all note folders
 */
export function useAllNoteFolders() {
  return useQuery({
    queryKey: queryKeys.notes.folders.list(),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('note_folders')
        .select('*')
        .order('name');

      if (error) throw error;
      return data as NoteFolder[];
    },
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

/**
 * Hook to fetch hierarchical folder structure for a specific project
 */
export function useFolderStructure(projectId?: string | number | null, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.notes.folders.structure(projectId),
    queryFn: async () => {
      // Fetch folders for the specific project
      const query = supabase
        .from('note_folders')
        .select('*')
        .order('name', { ascending: true });

      // Only filter by project_id if it's provided
      if (projectId) {
        query.eq('project_id', projectId);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Convert to nested structure
      const folderMap = new Map<number, FolderWithChildren>();
      const rootFolders: FolderWithChildren[] = [];

      // First, create all folder objects
      data.forEach((folder: NoteFolder) => {
        folderMap.set(folder.id, { ...folder, children: [] });
      });

      // Then, establish parent-child relationships
      data.forEach((folder: NoteFolder) => {
        const folderWithChildren = folderMap.get(folder.id);

        if (folder.parent_folder_id && folderMap.has(folder.parent_folder_id)) {
          // This is a child folder
          const parentFolder = folderMap.get(folder.parent_folder_id);
          if (parentFolder && parentFolder.children) {
            parentFolder.children.push(folderWithChildren as FolderWithChildren);
          }
        } else {
          // This is a root folder
          rootFolders.push(folderWithChildren as FolderWithChildren);
        }
      });

      return rootFolders;
    },
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    enabled: options?.enabled !== undefined ? options.enabled : true, // Default to true if not specified
  });
}

/**
 * Hook to create a note folder
 */
export function useCreateNoteFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      name,
      description = null,
      parentFolderId = null,
      projectId,
      tenantId
    }: {
      name: string;
      description?: string | null;
      parentFolderId?: number | null;
      projectId: string | number;
      tenantId: string;
    }) => {
      if (!tenantId) throw new Error('Tenant ID is required');

      // Create the note folder with explicit tenant_id and project_id
      const { data, error } = await supabase
        .from('note_folders')
        .insert([
          {
            name,
            description,
            parent_folder_id: parentFolderId,
            tenant_id: tenantId,
            project_id: projectId
          }
        ])
        .select()
        .single();

      if (error) throw error;
      return data as NoteFolder;
    },
    onSuccess: (_, variables) => {
      // Invalidate folder queries
      queryClient.invalidateQueries({
        queryKey: queryKeys.notes.folders.list(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.notes.folders.structure(variables.projectId),
      });
    },
  });
}

/**
 * Hook to update a note folder
 */
export function useUpdateNoteFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      folderId,
      data
    }: {
      folderId: number;
      data: Partial<NoteFolder>;
    }) => {
      const { data: updatedFolder, error } = await supabase
        .from('note_folders')
        .update(data)
        .eq('id', folderId)
        .select()
        .single();

      if (error) throw error;
      return updatedFolder as NoteFolder;
    },
    onSuccess: () => {
      // Invalidate folder queries
      queryClient.invalidateQueries({
        queryKey: queryKeys.notes.folders.list(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.notes.folders.structure(),
      });
    },
  });
}

/**
 * Hook to delete a note folder
 */
export function useDeleteNoteFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (folderId: number) => {
      const { error } = await supabase
        .from('note_folders')
        .delete()
        .eq('id', folderId);

      if (error) throw error;
      return folderId;
    },
    onSuccess: (folderId) => {
      // Invalidate folder queries
      queryClient.invalidateQueries({
        queryKey: queryKeys.notes.folders.list(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.notes.folders.structure(),
      });

      // Also invalidate any notes in this folder
      queryClient.invalidateQueries({
        queryKey: queryKeys.notes.byFolder(folderId),
      });
    },
  });
}

/**
 * Hook to fetch notes in a folder with optimized fields
 * Only fetches id and title for efficiency
 */
export function useFolderNotes(folderId: number | null | undefined, projectId?: string | number | null) {
  return useQuery({
    queryKey: projectId
      ? [...queryKeys.notes.byFolder(Number(folderId)), projectId.toString()]
      : queryKeys.notes.byFolder(Number(folderId)),
    queryFn: async () => {
      // Case 1: Fetching notes for a specific folder
      if (folderId) {
        let query = supabase
          .from('folder_notes')
          .select(`
            id,
            note_id,
            folder_id,
            position,
            created_at,
            project_id,
            note:note_id (id, title, created_at, updated_at, is_pinned, content)
          `)
          .eq('folder_id', folderId)
          .order('position');

        if (projectId) {
          query.eq('project_id', projectId);
        }

        const { data, error } = await query;

        if (error) throw error;

        // Extract the notes from the relationships
        const notes: Note[] = [];

        for (const item of data) {
          if (item.note && typeof item.note === 'object') {
            // For consistency, match the Note structure
            notes.push(item.note as unknown as Note);
          }
        }
        return notes;
      }

      // Case 2: Fetching root notes (not in any folder)
      // We look for notes that do NOT have a corresponding entry in 'folder_notes'
      // This is a "Right Anti Join" or "Not Exists" equivalent
      // In Supabase/PostgREST, we can use !inner on a left join and filter for nulls?
      // Actually simpler: Fetch all notes for project, and filter out those that are in folders.
      // But for better performance, let's use the relation if possible.
      // Let's try: select *, folder_notes(id) and filter where folder_notes is empty.

      let query = supabase
        .from('notes')
        .select(`
          *,
          folder_notes (id)
        `)
        .order('updated_at', { ascending: false });

      if (projectId) {
        query.eq('project_id', projectId);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Client-side filter for now to ensure we only get notes not in folders
      // @ts-ignore
      const rootNotes = data.filter(note => !note.folder_notes || note.folder_notes.length === 0);

      return rootNotes as Note[];
    },
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    enabled: folderId !== undefined,
  });
}

/**
 * Hook to add a note to a folder
 */
export function useAddNoteToFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      noteId,
      folderId,
      position = 0,
      projectId
    }: {
      noteId: number;
      folderId: number;
      position?: number;
      projectId: string | number;
    }) => {
      // First get the note
      const { data: noteData, error: noteError } = await supabase
        .from('notes')
        .select('*')
        .eq('id', noteId)
        .single();

      if (noteError) throw noteError;

      // Then create the relationship with project_id
      const { data, error } = await supabase
        .from('folder_notes')
        .insert([
          {
            note_id: noteId,
            folder_id: folderId,
            position,
            project_id: projectId
          }
        ])
        .select()
        .single();

      if (error) throw error;

      return {
        note: noteData as Note,
        folderNote: data as FolderNote
      };
    },
    onSuccess: (_, variables) => {
      // Invalidate the folder notes query
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.notes.byFolder(variables.folderId), variables.projectId.toString()],
      });
    },
  });
}

/**
 * Hook to remove a note from a folder
 */
export function useRemoveNoteFromFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      noteId,
      folderId
    }: {
      noteId: number;
      folderId: number;
    }) => {
      const { error } = await supabase
        .from('folder_notes')
        .delete()
        .eq('note_id', noteId)
        .eq('folder_id', folderId);

      if (error) throw error;
      return { noteId, folderId };
    },
    onSuccess: (_, variables) => {
      // Invalidate the folder notes query
      queryClient.invalidateQueries({
        queryKey: queryKeys.notes.byFolder(variables.folderId),
      });
    },
  });
}

/**
 * Hook to update a note's position within a folder
 */
export function useUpdateNotePosition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      noteId,
      folderId,
      newPosition
    }: {
      noteId: number;
      folderId: number;
      newPosition: number;
    }) => {
      const { data, error } = await supabase
        .from('folder_notes')
        .update({ position: newPosition })
        .eq('note_id', noteId)
        .eq('folder_id', folderId)
        .select()
        .single();

      if (error) throw error;
      return data as FolderNote;
    },
    onSuccess: (_, variables) => {
      // Invalidate the folder notes query
      queryClient.invalidateQueries({
        queryKey: queryKeys.notes.byFolder(variables.folderId),
      });
    },
  });
}
